import { instance } from "@/util/Alerts";

import { ActionContext } from "vuex";
type AluminumContext = ActionContext<AluminumState, RootState>;


const state = (): AluminumState => ({
    statusSelect: "active",
    statusOptions: [
        { value: "active", label: "Active", disabled: false },
        { value: "inactive", label: "Inactive", disabled: false },
        { value: "de-active", label: "De-active", disabled: false },
        { value: "test", label: "Test", disabled: false }
    ] as BaseDropdownOption[],
    seriesSelect: "",
    seriesOptions: [] as BaseDropdownOption[],
    sizeSelect: "",
    sizeOptions: [] as BaseDropdownOption[],
    voltageSelect: "",
    voltageOptions: [] as BaseDropdownOption[],
    capacitorList: [] as CapacitorModel[],
});

const getters = {
};

const mutations = {
    setStatusSelect(state: AluminumState, payload: string) {
        state.statusSelect = payload;
    },
    setSeriesSelect(state: AluminumState, payload: string) {
        state.seriesSelect = payload;
    },
    setSizeSelect(state: AluminumState, payload: string) {
        state.sizeSelect = payload;
    },
    setVoltageSelect(state: AluminumState, payload: string) {
        state.voltageSelect = payload;
    },
    setSeriesOptions(state: AluminumState, payload: BaseDropdownOption[]) {
        state.seriesOptions = payload;
    },
    setSizeOptions(state: AluminumState, payload: BaseDropdownOption[]) {
        state.sizeOptions = payload;
    },
    setVoltageOptions(state: AluminumState, payload: BaseDropdownOption[]) {
        state.voltageOptions = payload;
    },
    setCapacitors(state: AluminumState, payload: CapacitorModel[]) {
        state.capacitorList = payload;
    },
};

const actions = {
    setStatusSelect(context: AluminumContext, payload: string) {
        context.commit("setStatusSelect", payload);
    },
    setSeriesSelect(context: AluminumContext, payload: string) {
        context.commit("setSeriesSelect", payload);
    },
    setSizeSelect(context: AluminumContext, payload: string) {
        context.commit("setSizeSelect", payload);
    },
    setVoltageSelect(context: AluminumContext, payload: string) {
        context.commit("setVoltageSelect", payload);
    },
    getSeriesOptions(context: AluminumContext) {
        const url = "series";
        const body = {
            type: "aluminum",
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setSeriesOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                const value = response.data[i]["Style"];
                const label = response.data[i]["Description"];
                options.push({
                    value,
                    label: value + ": " + label,
                    disabled: false
                } as BaseDropdownOption);
            }
            context.commit("setSeriesOptions", options);
        }).catch(() => {
            context.commit("setSeriesOptions", [] as BaseDropdownOption[]);
        });
    },
    getSizeOptions(context: AluminumContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "")
        ) {
            context.commit("setSizeOptions", [] as BaseDropdownOption[]);
            return;
        }
        const url = "size";
        const body = {
            type: "aluminum",
            series: context.state.seriesSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setSizeOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                const value = response.data[i];
                const label = response.data[i];
                options.push({
                    value,
                    label,
                    disabled: false
                });
            }
            context.commit("setSizeOptions", options);
        }).catch(() => {
            context.commit("setSizeOptions", [] as BaseDropdownOption[]);
        });
    },
    getVoltageOptions(context: AluminumContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "" ||
                context.state.sizeSelect === "")
        ) {
            context.commit("setVoltageOptions", [] as BaseDropdownOption[]);
            return;
        }
        /* We can at least check for any of the body properties == "" and prevent
           an API call which we already know would return an empty response.
           We will need to check for empty responses, because empty responses
           can result from invalid selections (e.g. an invalid size selection
           due to the dropdowns still updating.
           Ideally, we create a flag for the component when all loading has finished.
           Need to implement some kind of loading indication anyways.
           */
        const url = "voltage";
        const body = {
            type: "aluminum",
            series: context.state.seriesSelect,
            size: context.state.sizeSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const options = [] as BaseDropdownOption[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setVoltageOptions", options);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                // To match SQL query we need 6 decimal points.
                const value = response.data[i].toFixed(6).toString();
                const label = response.data[i].toString();
                options.push({
                    value,
                    label,
                    disabled: false
                });
            }
            context.commit("setVoltageOptions", options);
        }).catch(() => {
            context.commit("setVoltageOptions", [] as BaseDropdownOption[]);
        });
    },
    getCapacitors(context: AluminumContext) {
        if (
            context.state.statusSelect === "active" &&
            (context.state.seriesSelect === "" ||
                context.state.sizeSelect === "" ||
                context.state.voltageSelect === "")
        ) {
            context.commit("setCapacitors", [] as CapacitorModel[]);
            return;
        }
        /* We can at least check for any of the body properties == "" and prevent
           an API call which we already know would return an empty response.
           We will need to check for empty responses, because empty responses
           can result from invalid selections (e.g. an invalid size selection
           due to the dropdowns still updating.
           Ideally, we create a flag for the component when all loading has finished.
           Need to implement some kind of loading indication anyways.
           */
        const url = "capacitance";
        const body = {
            type: "aluminum",
            series: context.state.seriesSelect,
            size: context.state.sizeSelect,
            voltage: context.state.voltageSelect,
            partStatus: context.state.statusSelect
        };
        instance.post(url, body).then((response) => {
            const capacitorList = [] as CapacitorModel[];
            if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
                context.commit("setCapacitors", capacitorList);
                return;
            }
            for (let i = 0; i < response.data.length; i++) {
                capacitorList.push({
                    id: response.data[i].id,
                    instances: 1,
                    capDisplay: response.data[i].capDisplay,
                    capType: response.data[i].capType,
                    capValue: response.data[i].capValue,
                    dielectric: response.data[i].dielectric,
                    maxTemp: response.data[i].maxTemp,
                    kemetPn: response.data[i].kemetPn,
                    basePn: response.data[i].basePn,
                    series: response.data[i].series,
                    appDef: response.data[i].appDef,
                    spicePn: response.data[i].spicePn,
                    voltageRating: response.data[i].voltageRating,
                    ceProps: [] as UpdatedCapCeProps,
                    hidden: false,
                    param: response.data[i].param,
                    tolerance: {
                        title: "Tolerance",
                        select: "n/a",
                        disabled: true,
                        hidden: false,
                        options: [
                            { value: "n/a", label: "n/a", disabled: false },
                        ],
                    } as InputDropdown,
                    filmProps: {} as FilmProps,
                    seedProps: [] as SeedProp[],
                    displayAllCeProps: false,
                    tcc: response.data[i].tcc,
                    vcac: response.data[i].vcac,
                });
            }
            context.commit("setCapacitors", capacitorList);
        }).catch(() => {
            context.commit("setCapacitors", [] as CapacitorModel[]);
        });
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}